// Modules
import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { playChatNotificationSound } from 'utils/chatNotificationSound'

// Store
import { isAuthenticatedSelector } from 'store/actions/Auth/selectors'
import { openChat, setChatUnreadCount } from 'store/actions/Chat/actions'
import { chatStateSelector, chatUnreadCountSelector } from 'store/actions/Chat/selectors'
import { getUnreadCountAPI, markAsReadAPI } from 'store/api/supportChat'

const POLLING_INTERVAL = 10000 // 10 seconds

// Единственный поллер непрочитанных сообщений чата на всё приложение.
// Держит счётчик в Redux (state.chat.unreadCount), откуда его читают
// Navbar / NavbarMobile. Пока вкладка в фоне — запросов нет.
const useChatUnreadPolling = () => {
  const dispatch = useDispatch()
  const isAuthenticated = useSelector(isAuthenticatedSelector)
  const isChatOpen = useSelector(chatStateSelector)
  const unreadCount = useSelector(chatUnreadCountSelector)

  const isChatOpenRef = useRef(isChatOpen)
  // null = ещё не было ни одного успешного запроса (не автооткрываем чат
  // из-за непрочитанных, накопившихся до загрузки страницы)
  const prevCountRef = useRef(null)

  useEffect(() => {
    isChatOpenRef.current = isChatOpen
  }, [isChatOpen])

  useEffect(() => {
    if (!isAuthenticated) return undefined

    const fetchUnreadCount = async () => {
      if (document.hidden) return

      try {
        const response = await getUnreadCountAPI()
        if (!response.success) return

        const count = response.data.unread_count
        dispatch(setChatUnreadCount(count))

        const isFirstFetch = prevCountRef.current === null

        // Новое сообщение пришло, пока чат закрыт — звук + автооткрытие
        if (!isFirstFetch && !isChatOpenRef.current && count > prevCountRef.current) {
          playChatNotificationSound()
          dispatch(openChat())
        }

        prevCountRef.current = count
      } catch (error) {
        console.error('Ошибка загрузки непрочитанных:', error)
      }
    }

    fetchUnreadCount()
    const interval = setInterval(fetchUnreadCount, POLLING_INTERVAL)

    const handleVisibilityChange = () => {
      if (!document.hidden) fetchUnreadCount()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isAuthenticated, dispatch])

  // Чат открыт — отмечаем непрочитанные как прочитанные
  useEffect(() => {
    if (!isChatOpen || unreadCount === 0) return

    markAsReadAPI()
      .then((response) => {
        if (response.success) {
          prevCountRef.current = 0
          dispatch(setChatUnreadCount(0))
        }
      })
      .catch((error) => console.error('Ошибка отметки как прочитанные:', error))
  }, [isChatOpen, unreadCount, dispatch])
}

export default useChatUnreadPolling
